import { useNavigate } from 'react-router-dom';
import { QUOTE_STATUS_LABELS } from '@catalyst-psa/shared';

import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { DataState } from '../../components/ui/DataState.js';
import { formatDate } from '../../utils/date.js';
import { useCreateQuote, useQuotes } from './api.js';
import { QUOTE_STATUS_TONES } from './status.js';

/**
 * Quotes tab inside the opportunity editor — lists the opportunity's quote versions and
 * is the ONLY place new quotes are created (a new version of the single quote family).
 */
export function OpportunityQuotesSection({
  opportunityId,
  canEdit,
}: {
  opportunityId: string;
  canEdit: boolean;
}): JSX.Element {
  const navigate = useNavigate();
  const query = useQuotes({ page: 1, pageSize: 100, opportunityId });
  const quotes = (query.data?.data ?? []).slice().sort((a, b) => b.version - a.version);
  const createMutation = useCreateQuote();

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">
          {quotes.length === 0
            ? 'No quotes yet for this opportunity.'
            : 'One quote per opportunity — newer versions supersede older ones.'}
        </p>
        {canEdit ? (
          <Button
            size="sm"
            isLoading={createMutation.isPending}
            onClick={() =>
              createMutation.mutate(opportunityId, {
                onSuccess: (created) => navigate(`/sales/quotes/${created.id}`),
              })
            }
          >
            {quotes.length === 0 ? 'New quote' : 'New version'}
          </Button>
        ) : null}
      </div>

      <div className="rounded-lg border border-slate-200">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-3 py-2 font-medium">Version</th>
              <th className="px-3 py-2 font-medium">Status</th>
              <th className="px-3 py-2 font-medium">Updated</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            <DataState
              isLoading={query.isLoading}
              isError={query.isError}
              error={query.error}
              onRetry={() => void query.refetch()}
              isEmpty={!query.isLoading && quotes.length === 0}
              emptyTitle="No quotes"
              emptyDescription="Create the first quote for this opportunity."
              colSpan={3}
            >
              {quotes.map((quote) => (
                <tr
                  key={quote.id}
                  onClick={() => navigate(`/sales/quotes/${quote.id}`)}
                  className="cursor-pointer hover:bg-slate-50"
                >
                  <td className="px-3 py-2 font-medium text-slate-800">
                    v{quote.version}
                    {quote.isLatest ? (
                      <span className="ml-2 inline-flex">
                        <Badge tone="blue">Latest</Badge>
                      </span>
                    ) : null}
                  </td>
                  <td className="px-3 py-2">
                    <Badge tone={QUOTE_STATUS_TONES[quote.status]}>
                      {QUOTE_STATUS_LABELS[quote.status]}
                    </Badge>
                  </td>
                  <td className="px-3 py-2 text-slate-500">{formatDate(quote.updatedAt)}</td>
                </tr>
              ))}
            </DataState>
          </tbody>
        </table>
      </div>
    </div>
  );
}
